import React from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Tabs, Tab, useTheme, useMediaQuery } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Atendimento from "../../components/Atendimento";
import FinanceiroAtendimento from "../../components/FinanceiroAtendimento";
import VendaProdutos from "../../components/VendaProdutos";
import CustomerAwareness from "../../components/CustomerAwareness";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import Produtos from "../../components/Produtos";
import VendaPassagens from "../../components/VendaPassagens";
import SurveyAnalysis from "../../components/SurveyAnalysis";
import CampaignOriginDashboard from "../../components/CampaignOriginDashboard";
import DashboardLiveTab from "./DashboardLiveTab";
import DashboardTasksTab from "./DashboardTasksTab";
import { useWorkspaceSegment } from "../../hooks/useWorkspaceSegment";
import { WorkspaceSegments } from "../../constants/workspaceSegments";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getBillingOffers, exportDashboardCsv } from "../../api/client";
import {
  readDashboardDate,
  writeDashboardDate,
  readDashboardAdvancedFilters,
  writeDashboardAdvancedFilters,
  readDashboardCampaignFilters,
  writeDashboardCampaignFilters,
  readDashboardSalesFilters,
  writeDashboardSalesFilters,
  readDashboardClientsFilters,
  writeDashboardClientsFilters,
  readDashboardProdutosFilters,
  writeDashboardProdutosFilters,
  readDashboardPassagensFilters,
  writeDashboardPassagensFilters,
  readDashboardSurveysFilters,
  writeDashboardSurveysFilters,
} from "../../utils/workspaceStorage";
import { applyDayjsLocale, datePickerLocalizationForI18n } from "../../utils/dayjsLocale";
import { applyDashboardDatesFromSearchParams } from "../../utils/dashboardDeepLink";
import {
  getInitialDashboardDateRange,
  getDashboardInclusiveSpanDays,
  normalizeDashboardDateRange,
} from "../../utils/dashboardDateRange";
import { useQueryClient } from "react-query";
import { DashboardJobBootstrapContext } from "../../context/DashboardJobBootstrapContext";
import { DashboardJobsProvider } from "../../context/DashboardJobsContext";
import DashboardBackgroundJobsPanel from "../../components/dashboard/DashboardBackgroundJobsPanel";
import DashboardPageToolbar from "../../components/dashboard/DashboardPageToolbar";
import DashboardPresentationShareDialog from "../../components/dashboard/DashboardPresentationShareDialog";
import { DashboardShareReadinessProvider } from "../../context/DashboardShareReadinessContext";
import DashboardCachesDialog from "../../components/dashboard/DashboardCachesDialog";
import AdvancedSearchModal from "../../components/AdvancedSearchModal";
import DashboardAdvancedFiltersControl from "../../components/dashboard/DashboardAdvancedFiltersControl";
import DashboardCampaignFiltersControl from "../../components/dashboard/DashboardCampaignFiltersControl";
import DashboardCampaignFiltersModal from "../../components/dashboard/DashboardCampaignFiltersModal";
import DashboardSalesFiltersControl from "../../components/dashboard/DashboardSalesFiltersControl";
import DashboardSalesFiltersModal from "../../components/dashboard/DashboardSalesFiltersModal";
import DashboardClientsFiltersControl from "../../components/dashboard/DashboardClientsFiltersControl";
import DashboardClientsFiltersModal from "../../components/dashboard/DashboardClientsFiltersModal";
import DashboardProdutosFiltersControl from "../../components/dashboard/DashboardProdutosFiltersControl";
import DashboardProdutosFiltersModal from "../../components/dashboard/DashboardProdutosFiltersModal";
import DashboardPassagensFiltersControl from "../../components/dashboard/DashboardPassagensFiltersControl";
import DashboardPassagensFiltersModal from "../../components/dashboard/DashboardPassagensFiltersModal";
import DashboardSurveysFiltersControl from "../../components/dashboard/DashboardSurveysFiltersControl";
import DashboardSurveysFiltersModal from "../../components/dashboard/DashboardSurveysFiltersModal";
import { useAuthContext } from "../../context/AuthContext";
import {
  can,
  scopeSetFromPermissions,
  useEffectivePermissions,
} from "../../hooks/useEffectivePermissions";
import { useDashboardFinanceAttendanceReadiness } from "../../hooks/useDashboardFinanceAttendanceReadiness";
import {
  EMPTY_SEARCH_FILTER_STATE,
  normalizeSearchFilterState,
  modalPayloadToFilterState,
  filterStateToModalInitial,
  filterStateToDashboardParams,
  filterStateToLiveAdvancedParams,
  countDashboardAdvancedCriteria,
} from "../../utils/searchFilterState";
import {
  EMPTY_CAMPAIGN_FILTER_STATE,
  normalizeCampaignFilterState,
  countCampaignFilterCriteria,
  campaignFilterStateToQueryParams,
} from "../../utils/dashboardCampaignFilterState";
import {
  EMPTY_SALES_FILTER_STATE,
  normalizeSalesFilterState,
  countSalesFilterCriteria,
  salesFilterStateToQueryParams,
} from "../../utils/dashboardSalesFilterState";
import {
  EMPTY_CLIENTS_FILTER_STATE,
  normalizeClientsFilterState,
  countClientsFilterCriteria,
  clientsFilterStateToQueryParams,
} from "../../utils/dashboardClientsFilterState";
import {
  EMPTY_PRODUTOS_FILTER_STATE,
  normalizeProdutosFilterState,
  countProdutosFilterCriteria,
  produtosFilterStateToQueryParams,
} from "../../utils/dashboardProdutosFilterState";
import {
  EMPTY_PASSAGENS_FILTER_STATE,
  normalizePassagensFilterState,
  countPassagensFilterCriteria,
  passagensFilterStateToQueryParams,
} from "../../utils/dashboardPassagensFilterState";
import {
  EMPTY_SURVEYS_FILTER_STATE,
  normalizeSurveysFilterState,
  countSurveysFilterCriteria,
  surveysFilterStateToAnalyticsParams,
} from "../../utils/dashboardSurveysFilterState";

dayjs.extend(localizedFormat);
applyDayjsLocale("pt-BR");

const initialDashboardDates = getInitialDashboardDateRange(readDashboardDate);

function readInitialAdvancedFilterState() {
  try {
    const stored = readDashboardAdvancedFilters();
    if (stored && typeof stored === "object") {
      return normalizeSearchFilterState({ ...EMPTY_SEARCH_FILTER_STATE, ...stored, q: "" });
    }
  } catch (e) {}
  return normalizeSearchFilterState(EMPTY_SEARCH_FILTER_STATE);
}

function readInitialCampaignFilterState() {
  try {
    const stored = readDashboardCampaignFilters();
    if (stored && typeof stored === "object") {
      return normalizeCampaignFilterState(stored);
    }
  } catch (e) {}
  return normalizeCampaignFilterState(EMPTY_CAMPAIGN_FILTER_STATE);
}

function readInitialSalesFilterState() {
  try {
    const stored = readDashboardSalesFilters();
    if (stored && typeof stored === "object") {
      return normalizeSalesFilterState(stored);
    }
  } catch (e) {}
  return normalizeSalesFilterState(EMPTY_SALES_FILTER_STATE);
}

function readInitialClientsFilterState() {
  try {
    const stored = readDashboardClientsFilters();
    if (stored && typeof stored === "object") {
      return normalizeClientsFilterState(stored);
    }
  } catch (e) {}
  return normalizeClientsFilterState(EMPTY_CLIENTS_FILTER_STATE);
}

function readInitialProdutosFilterState() {
  try {
    const stored = readDashboardProdutosFilters();
    if (stored && typeof stored === "object") {
      return normalizeProdutosFilterState(stored);
    }
  } catch (e) {}
  return normalizeProdutosFilterState(EMPTY_PRODUTOS_FILTER_STATE);
}

function readInitialPassagensFilterState() {
  try {
    const stored = readDashboardPassagensFilters();
    if (stored && typeof stored === "object") {
      return normalizePassagensFilterState(stored);
    }
  } catch (e) {}
  return normalizePassagensFilterState(EMPTY_PASSAGENS_FILTER_STATE);
}

function readInitialSurveysFilterState() {
  try {
    const stored = readDashboardSurveysFilters();
    if (stored && typeof stored === "object") {
      return normalizeSurveysFilterState(stored);
    }
  } catch (e) {}
  return normalizeSurveysFilterState(EMPTY_SURVEYS_FILTER_STATE);
}

function persistDashboardDates(start, end) {
  try {
    if (start?.isValid?.()) writeDashboardDate("start", start.format("YYYY-MM-DD"));
    if (end?.isValid?.()) writeDashboardDate("end", end.format("YYYY-MM-DD"));
  } catch (e) {}
}

function applyNormalizedRange(setStartDate, setEndDate, start, end, changed, priorSpanDays) {
  const normalized = normalizeDashboardDateRange(start, end, { changed, priorSpanDays });
  setStartDate(normalized.start);
  setEndDate(normalized.end);
  persistDashboardDates(normalized.start, normalized.end);
  return normalized;
}

export function dashboardTabPath(tabKey, search) {
  const pathname = `/dashboard/${tabKey}`;
  if (search == null || search === "") return pathname;
  const qs = String(search).startsWith("?") ? String(search) : `?${search}`;
  return `${pathname}${qs}`;
}

const Dashboard = () => {
  const { t, i18n } = useTranslation("settings");
  const { userData } = useAuthContext();
  const { data: permissionsData, isLoading: permissionsLoading } = useEffectivePermissions();
  const permissionScopes = React.useMemo(
    () => scopeSetFromPermissions(permissionsData, userData),
    [permissionsData, userData]
  );
  const canViewCampaigns =
    permissionsData?.features?.campaigns === true &&
    can(permissionScopes, "campaigns", "view");
  const { data: financeAttendanceReady = false } = useDashboardFinanceAttendanceReadiness();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { tab: tabParam } = useParams();
  const [searchParams] = useSearchParams();
  const { segment } = useWorkspaceSegment();
  const [startDate, setStartDate] = React.useState(initialDashboardDates.start);
  const [endDate, setEndDate] = React.useState(initialDashboardDates.end);
  const [bootstrapJobId, setBootstrapJobId] = React.useState(null);
  const [advancedFilterState, setAdvancedFilterState] = React.useState(readInitialAdvancedFilterState);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = React.useState(false);
  const [campaignFilterState, setCampaignFilterState] = React.useState(readInitialCampaignFilterState);
  const [campaignFiltersOpen, setCampaignFiltersOpen] = React.useState(false);
  const [salesFilterState, setSalesFilterState] = React.useState(readInitialSalesFilterState);
  const [salesFiltersOpen, setSalesFiltersOpen] = React.useState(false);
  const [clientsFilterState, setClientsFilterState] = React.useState(readInitialClientsFilterState);
  const [clientsFiltersOpen, setClientsFiltersOpen] = React.useState(false);
  const [produtosFilterState, setProdutosFilterState] = React.useState(readInitialProdutosFilterState);
  const [produtosFiltersOpen, setProdutosFiltersOpen] = React.useState(false);
  const [passagensFilterState, setPassagensFilterState] = React.useState(readInitialPassagensFilterState);
  const [passagensFiltersOpen, setPassagensFiltersOpen] = React.useState(false);
  const [surveysFilterState, setSurveysFilterState] = React.useState(readInitialSurveysFilterState);
  const [surveysFiltersOpen, setSurveysFiltersOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Detecta mobile
  const [billingSignal, setBillingSignal] = React.useState(null);
  const [shareOpen, setShareOpen] = React.useState(false);
  const [cachesOpen, setCachesOpen] = React.useState(false);
  const maxSelectableDate = React.useMemo(() => dayjs().endOf("day"), []);

  const atendimentoFilterParams = React.useMemo(() => {
    const fromState = filterStateToDashboardParams(advancedFilterState);
    if (fromState.all_period) {
      // Todo período: não injeta datas da toolbar.
      return { ...fromState };
    }
    return {
      ...fromState,
      start: fromState.start || startDate.format("YYYY-MM-DD"),
      end: fromState.end || endDate.format("YYYY-MM-DD"),
    };
  }, [advancedFilterState, startDate, endDate]);

  const liveAdvancedParams = React.useMemo(() => {
    const statusFilters = (advancedFilterState.filters || []).filter((f) =>
      ["Pendente", "Em Atendimento", "Contato Futuro", "Concluída", "Perdida", "Cancelada"].includes(f)
    );
    const criteriaCount = countDashboardAdvancedCriteria(advancedFilterState, {
      includeDates: false,
    });
    const hasDateFieldOverride = advancedFilterState.date_field === "atualizacao";
    const isAllPeriod = Boolean(advancedFilterState.all_period);
    if (criteriaCount === 0 && !hasDateFieldOverride && statusFilters.length === 0) {
      return null;
    }
    const stateWithDates = isAllPeriod
      ? { ...advancedFilterState, data_inicio: "", data_fim: "" }
      : {
          ...advancedFilterState,
          data_inicio: startDate.format("YYYY-MM-DD"),
          data_fim: endDate.format("YYYY-MM-DD"),
        };
    const adv = filterStateToLiveAdvancedParams(stateWithDates) || {};
    return {
      ...adv,
      statusFilters,
      ...(isAllPeriod ? { default_date_window_days: 0 } : {}),
    };
  }, [advancedFilterState, startDate, endDate]);

  const advancedCriteriaCount = React.useMemo(
    () => countDashboardAdvancedCriteria(advancedFilterState, { includeDates: false }),
    [advancedFilterState]
  );

  const campaignCriteriaCount = React.useMemo(
    () => countCampaignFilterCriteria(campaignFilterState),
    [campaignFilterState]
  );

  const campaignQueryParams = React.useMemo(
    () => campaignFilterStateToQueryParams(campaignFilterState),
    [campaignFilterState]
  );

  const salesCriteriaCount = React.useMemo(
    () => countSalesFilterCriteria(salesFilterState),
    [salesFilterState]
  );

  const salesQueryParams = React.useMemo(
    () => salesFilterStateToQueryParams(salesFilterState),
    [salesFilterState]
  );

  const clientsCriteriaCount = React.useMemo(
    () => countClientsFilterCriteria(clientsFilterState),
    [clientsFilterState]
  );

  const clientsQueryParams = React.useMemo(
    () => clientsFilterStateToQueryParams(clientsFilterState),
    [clientsFilterState]
  );

  const produtosCriteriaCount = React.useMemo(
    () => countProdutosFilterCriteria(produtosFilterState),
    [produtosFilterState]
  );
  const produtosQueryParams = React.useMemo(
    () => produtosFilterStateToQueryParams(produtosFilterState),
    [produtosFilterState]
  );

  const passagensCriteriaCount = React.useMemo(
    () => countPassagensFilterCriteria(passagensFilterState),
    [passagensFilterState]
  );
  const passagensQueryParams = React.useMemo(
    () => passagensFilterStateToQueryParams(passagensFilterState),
    [passagensFilterState]
  );

  const surveysCriteriaCount = React.useMemo(
    () => countSurveysFilterCriteria(surveysFilterState),
    [surveysFilterState]
  );
  const surveysAnalyticsParams = React.useMemo(
    () => surveysFilterStateToAnalyticsParams(surveysFilterState),
    [surveysFilterState]
  );

  const persistAdvancedFilters = React.useCallback((next) => {
    const normalized = normalizeSearchFilterState({ ...next, q: "" });
    setAdvancedFilterState(normalized);
    try {
      if (countDashboardAdvancedCriteria(normalized, { includeDates: true }) === 0) {
        writeDashboardAdvancedFilters(null);
      } else {
        writeDashboardAdvancedFilters(normalized);
      }
    } catch (e) {}
  }, []);

  const handleAdvancedFiltersApply = React.useCallback(
    (payload) => {
      const next = modalPayloadToFilterState(payload);
      next.q = "";
      next.include_message_text = false;
      if (!next.all_period) {
        if (next.data_inicio) {
          const d = dayjs(next.data_inicio).startOf("day");
          if (d.isValid()) setStartDate(d);
        }
        if (next.data_fim) {
          const d = dayjs(next.data_fim).endOf("day");
          if (d.isValid()) setEndDate(d);
        }
        if (next.data_inicio || next.data_fim) {
          persistDashboardDates(
            next.data_inicio ? dayjs(next.data_inicio).startOf("day") : startDate,
            next.data_fim ? dayjs(next.data_fim).endOf("day") : endDate
          );
        }
      }
      setBootstrapJobId(null);
      persistAdvancedFilters(next);
    },
    [persistAdvancedFilters, startDate, endDate]
  );

  const handleClearAdvancedFilters = React.useCallback(() => {
    setBootstrapJobId(null);
    persistAdvancedFilters(EMPTY_SEARCH_FILTER_STATE);
  }, [persistAdvancedFilters]);

  const persistCampaignFilters = React.useCallback((next) => {
    const normalized = normalizeCampaignFilterState(next);
    setCampaignFilterState(normalized);
    try {
      if (countCampaignFilterCriteria(normalized) === 0) {
        writeDashboardCampaignFilters(null);
      } else {
        writeDashboardCampaignFilters(normalized);
      }
    } catch (e) {}
  }, []);

  const handleCampaignFiltersApply = React.useCallback(
    (next) => {
      persistCampaignFilters(next);
    },
    [persistCampaignFilters]
  );

  const handleClearCampaignFilters = React.useCallback(() => {
    persistCampaignFilters(EMPTY_CAMPAIGN_FILTER_STATE);
  }, [persistCampaignFilters]);

  const persistSalesFilters = React.useCallback((next) => {
    const normalized = normalizeSalesFilterState(next);
    setSalesFilterState(normalized);
    try {
      if (countSalesFilterCriteria(normalized) === 0) {
        writeDashboardSalesFilters(null);
      } else {
        writeDashboardSalesFilters(normalized);
      }
    } catch (e) {}
  }, []);

  const handleSalesFiltersApply = React.useCallback(
    (next) => {
      persistSalesFilters(next);
    },
    [persistSalesFilters]
  );

  const handleClearSalesFilters = React.useCallback(() => {
    persistSalesFilters(EMPTY_SALES_FILTER_STATE);
  }, [persistSalesFilters]);

  const persistClientsFilters = React.useCallback((next) => {
    const normalized = normalizeClientsFilterState(next);
    setClientsFilterState(normalized);
    try {
      if (countClientsFilterCriteria(normalized) === 0) {
        writeDashboardClientsFilters(null);
      } else {
        writeDashboardClientsFilters(normalized);
      }
    } catch (e) {}
  }, []);

  const handleClientsFiltersApply = React.useCallback(
    (next) => {
      persistClientsFilters(next);
    },
    [persistClientsFilters]
  );

  const handleClearClientsFilters = React.useCallback(() => {
    persistClientsFilters(EMPTY_CLIENTS_FILTER_STATE);
  }, [persistClientsFilters]);

  const persistProdutosFilters = React.useCallback((next) => {
    const normalized = normalizeProdutosFilterState(next);
    setProdutosFilterState(normalized);
    try {
      if (countProdutosFilterCriteria(normalized) === 0) writeDashboardProdutosFilters(null);
      else writeDashboardProdutosFilters(normalized);
    } catch (e) {}
  }, []);
  const handleProdutosFiltersApply = React.useCallback(
    (next) => persistProdutosFilters(next),
    [persistProdutosFilters]
  );
  const handleClearProdutosFilters = React.useCallback(() => {
    persistProdutosFilters(EMPTY_PRODUTOS_FILTER_STATE);
  }, [persistProdutosFilters]);

  const persistPassagensFilters = React.useCallback((next) => {
    const normalized = normalizePassagensFilterState(next);
    setPassagensFilterState(normalized);
    try {
      if (countPassagensFilterCriteria(normalized) === 0) writeDashboardPassagensFilters(null);
      else writeDashboardPassagensFilters(normalized);
    } catch (e) {}
  }, []);
  const handlePassagensFiltersApply = React.useCallback(
    (next) => persistPassagensFilters(next),
    [persistPassagensFilters]
  );
  const handleClearPassagensFilters = React.useCallback(() => {
    persistPassagensFilters(EMPTY_PASSAGENS_FILTER_STATE);
  }, [persistPassagensFilters]);

  const persistSurveysFilters = React.useCallback((next) => {
    const normalized = normalizeSurveysFilterState(next);
    setSurveysFilterState(normalized);
    try {
      if (countSurveysFilterCriteria(normalized) === 0) writeDashboardSurveysFilters(null);
      else writeDashboardSurveysFilters(normalized);
    } catch (e) {}
  }, []);
  const handleSurveysFiltersApply = React.useCallback(
    (next) => persistSurveysFilters(next),
    [persistSurveysFilters]
  );
  const handleClearSurveysFilters = React.useCallback(() => {
    persistSurveysFilters(EMPTY_SURVEYS_FILTER_STATE);
  }, [persistSurveysFilters]);

  const datePickerLocale = React.useMemo(
    () => datePickerLocalizationForI18n(i18n.language),
    [i18n.language]
  );

  React.useEffect(() => {
    persistDashboardDates(startDate, endDate);
    // Corrige intervalos inválidos salvos anteriormente no storage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    applyDayjsLocale(i18n.language);
  }, [i18n.language]);

  React.useEffect(() => {
    const applied = applyDashboardDatesFromSearchParams(searchParams, {
      onApply: (start, end) => {
        if (start) setStartDate(start);
        if (end) setEndDate(end);
        persistDashboardDates(start, end);
      },
    });
    const jobFromUrl = searchParams.get("job");
    if (jobFromUrl) {
      setBootstrapJobId(jobFromUrl);
      const next = new URLSearchParams(searchParams);
      next.delete("job");
      const qs = next.toString();
      navigate({ search: qs ? `?${qs}` : "" }, { replace: true });
    }
    if (applied || jobFromUrl) {
      queryClient.invalidateQueries(["dashboard"]);
      queryClient.invalidateQueries(["dashboard-preview"]);
      queryClient.invalidateQueries(["survey-analytics"]);
      queryClient.invalidateQueries(["survey-analytics-preview"]);
      queryClient.invalidateQueries(["dashboard-active-jobs"]);
    }
  }, [searchParams, queryClient, navigate]);

  React.useEffect(() => {
    if (!bootstrapJobId) return undefined;
    const timer = setTimeout(() => setBootstrapJobId(null), 45_000);
    return () => clearTimeout(timer);
  }, [bootstrapJobId]);

  React.useEffect(() => {
    let mounted = true;
    getBillingOffers({ autoGenerate: 1 })
      .then((res) => {
        if (!mounted) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        setBillingSignal(items[0] || null);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    const onLimitHit = (ev) => {
      const msg = ev?.detail?.message || "Seu plano atingiu um limite. Avalie upgrade ou adicional.";
      setBillingSignal({ payload: { message: msg } });
    };
    window.addEventListener("xpro:billing-limit-hit", onLimitHit);
    return () => window.removeEventListener("xpro:billing-limit-hit", onLimitHit);
  }, []);

  const handleStartDateChange = (newValue) => {
    if (!newValue) return;
    setBootstrapJobId(null);
    const d = dayjs(newValue).startOf("day");
    const priorSpanDays = getDashboardInclusiveSpanDays(startDate, endDate);
    applyNormalizedRange(setStartDate, setEndDate, d, endDate, "start", priorSpanDays);
  };

  const handleEndDateChange = (newValue) => {
    if (!newValue) return;
    setBootstrapJobId(null);
    const d = dayjs(newValue).endOf("day");
    const priorSpanDays = getDashboardInclusiveSpanDays(startDate, endDate);
    applyNormalizedRange(setStartDate, setEndDate, startDate, d, "end", priorSpanDays);
  };

  const handleNarrowToLast7Days = React.useCallback(() => {
    setBootstrapJobId(null);
    persistAdvancedFilters({
      ...advancedFilterState,
      all_period: false,
      data_inicio: "",
      data_fim: "",
    });
    const end = dayjs().endOf("day");
    const start = dayjs().subtract(6, "day").startOf("day");
    applyNormalizedRange(setStartDate, setEndDate, start, end);
  }, [advancedFilterState, persistAdvancedFilters]);

  const tabsDef = React.useMemo(() => {
    const base = [
      {
        key: "live",
        label: t("dashboardPage.tabLive"),
        section: "live",
        render: () => <DashboardLiveTab advancedListParams={liveAdvancedParams} />,
      },
      {
        key: "overview",
        label: t("dashboardPage.tabOverview"),
        section: "atendimento",
        render: () => (
          <Atendimento
            start={atendimentoFilterParams.start}
            end={atendimentoFilterParams.end}
            ids_origens={atendimentoFilterParams.ids_origens || []}
            ids_tipos={atendimentoFilterParams.ids_tipos || []}
            ids_subtipo1={atendimentoFilterParams.ids_subtipo1 || []}
            ids_subtipo2={atendimentoFilterParams.ids_subtipo2 || []}
            ids_subtipo3={atendimentoFilterParams.ids_subtipo3 || []}
            ids_status={atendimentoFilterParams.ids_status || []}
            ids_usuarios={atendimentoFilterParams.ids_usuarios || []}
            ids_agentes={atendimentoFilterParams.ids_agentes || []}
            ids_usuarios_interacao={atendimentoFilterParams.ids_usuarios_interacao || []}
            ids_agentes_interacao={atendimentoFilterParams.ids_agentes_interacao || []}
            tags={atendimentoFilterParams.tags || []}
            tipo_responsavel={atendimentoFilterParams.tipo_responsavel || ""}
            date_field={atendimentoFilterParams.date_field || "entrada"}
            all_period={Boolean(atendimentoFilterParams.all_period)}
            ids_produtos={atendimentoFilterParams.ids_produtos || []}
            ids_campaigns={atendimentoFilterParams.ids_campaigns || []}
            ids_whatsapp_campaigns={atendimentoFilterParams.ids_whatsapp_campaigns || []}
            ids_motivos={atendimentoFilterParams.ids_motivos || []}
            ids_potenciais={atendimentoFilterParams.ids_potenciais || []}
            ops_preset={atendimentoFilterParams.ops_preset || ""}
            stale_days={atendimentoFilterParams.stale_days}
            onNarrowPeriod={handleNarrowToLast7Days}
          />
        ),
      },
      {
        key: "service",
        label: t("dashboardPage.tabService"),
        section: "atendimento",
        render: () => (
          <Atendimento
            start={atendimentoFilterParams.start}
            end={atendimentoFilterParams.end}
            ids_origens={atendimentoFilterParams.ids_origens || []}
            ids_tipos={atendimentoFilterParams.ids_tipos || []}
            ids_subtipo1={atendimentoFilterParams.ids_subtipo1 || []}
            ids_subtipo2={atendimentoFilterParams.ids_subtipo2 || []}
            ids_subtipo3={atendimentoFilterParams.ids_subtipo3 || []}
            ids_status={atendimentoFilterParams.ids_status || []}
            ids_usuarios={atendimentoFilterParams.ids_usuarios || []}
            ids_agentes={atendimentoFilterParams.ids_agentes || []}
            ids_usuarios_interacao={atendimentoFilterParams.ids_usuarios_interacao || []}
            ids_agentes_interacao={atendimentoFilterParams.ids_agentes_interacao || []}
            tags={atendimentoFilterParams.tags || []}
            tipo_responsavel={atendimentoFilterParams.tipo_responsavel || ""}
            date_field={atendimentoFilterParams.date_field || "entrada"}
            all_period={Boolean(atendimentoFilterParams.all_period)}
            ids_produtos={atendimentoFilterParams.ids_produtos || []}
            ids_campaigns={atendimentoFilterParams.ids_campaigns || []}
            ids_whatsapp_campaigns={atendimentoFilterParams.ids_whatsapp_campaigns || []}
            ids_motivos={atendimentoFilterParams.ids_motivos || []}
            ids_potenciais={atendimentoFilterParams.ids_potenciais || []}
            ops_preset={atendimentoFilterParams.ops_preset || ""}
            stale_days={atendimentoFilterParams.stale_days}
            tipo_scope="relacionamento"
            onNarrowPeriod={handleNarrowToLast7Days}
          />
        ),
      },
      ...(financeAttendanceReady
        ? [
            {
              key: "financeAttendance",
              label: t("dashboardPage.tabFinanceAttendance"),
              section: "financeiro-atendimento",
              render: () => (
                <FinanceiroAtendimento
                  start={atendimentoFilterParams.start}
                  end={atendimentoFilterParams.end}
                  ids_origens={atendimentoFilterParams.ids_origens || []}
                  ids_tipos={atendimentoFilterParams.ids_tipos || []}
                  ids_subtipo1={atendimentoFilterParams.ids_subtipo1 || []}
                  ids_subtipo2={atendimentoFilterParams.ids_subtipo2 || []}
                  ids_subtipo3={atendimentoFilterParams.ids_subtipo3 || []}
                  ids_status={atendimentoFilterParams.ids_status || []}
                  ids_usuarios={atendimentoFilterParams.ids_usuarios || []}
                  ids_agentes={atendimentoFilterParams.ids_agentes || []}
                  ids_usuarios_interacao={atendimentoFilterParams.ids_usuarios_interacao || []}
                  ids_agentes_interacao={atendimentoFilterParams.ids_agentes_interacao || []}
                  tags={atendimentoFilterParams.tags || []}
                  tipo_responsavel={atendimentoFilterParams.tipo_responsavel || ""}
                  date_field={atendimentoFilterParams.date_field || "entrada"}
                  all_period={Boolean(atendimentoFilterParams.all_period)}
                  ids_produtos={atendimentoFilterParams.ids_produtos || []}
                  ids_campaigns={atendimentoFilterParams.ids_campaigns || []}
                  ids_whatsapp_campaigns={atendimentoFilterParams.ids_whatsapp_campaigns || []}
                  ids_motivos={atendimentoFilterParams.ids_motivos || []}
                  ids_potenciais={atendimentoFilterParams.ids_potenciais || []}
                  ops_preset={atendimentoFilterParams.ops_preset || ""}
                  stale_days={atendimentoFilterParams.stale_days}
                />
              ),
            },
          ]
        : []),
      {
        key: "clients",
        label: t("dashboardPage.tabClients"),
        section: "clientes",
        render: () => (
          <CustomerAwareness
            start={startDate.format("YYYY-MM-DD")}
            end={endDate.format("YYYY-MM-DD")}
            ids_grupos={clientsQueryParams.ids_grupos || []}
            ufs={clientsQueryParams.ufs || []}
            cidades={clientsQueryParams.cidades || []}
            tags={clientsQueryParams.tags || []}
            ids_potenciais={clientsQueryParams.ids_potenciais || []}
            date_field={clientsQueryParams.date_field || "cadastro"}
            all_period={Boolean(clientsQueryParams.all_period)}
          />
        ),
      },
      {
        key: "products",
        label: t("dashboardPage.tabProducts"),
        section: "produtos",
        // No segmento de transporte rodoviário, "Produtos" vira "Passagens"
        segments: [WorkspaceSegments.Generic, WorkspaceSegments.AutoParts],
        render: () => (
          <Produtos
            start={startDate.format("YYYY-MM-DD")}
            end={endDate.format("YYYY-MM-DD")}
            ids_categorias={produtosQueryParams.ids_categorias || []}
            ids_subcategorias={produtosQueryParams.ids_subcategorias || []}
            availability={produtosQueryParams.availability || ""}
            search={produtosQueryParams.search || ""}
          />
        ),
      },
      {
        key: "sales",
        label: t("dashboardPage.tabSales"),
        section: "vendas",
        render: () => (
          <VendaProdutos
            start={startDate.format("YYYY-MM-DD")}
            end={endDate.format("YYYY-MM-DD")}
            ids_status={salesQueryParams.ids_status || []}
            ids_origens={salesQueryParams.ids_origens || []}
            ids_categorias={salesQueryParams.ids_categorias || []}
            ids_subcategorias={salesQueryParams.ids_subcategorias || []}
            ids_motivos={salesQueryParams.ids_motivos || []}
            ids_usuarios={salesQueryParams.ids_usuarios || []}
            ids_agentes={salesQueryParams.ids_agentes || []}
            availability={salesQueryParams.availability || ""}
          />
        ),
      },
      {
        key: "busTickets",
        label: t("dashboardPage.tabBusTickets"),
        section: "venda-passagens",
        segments: [WorkspaceSegments.PassengerTransport],
        render: () => (
          <VendaPassagens
            start={startDate.format("YYYY-MM-DD")}
            end={endDate.format("YYYY-MM-DD")}
            origens={passagensQueryParams.origens || []}
            destinos={passagensQueryParams.destinos || []}
            ids_origens={passagensQueryParams.ids_origens || []}
            status={passagensQueryParams.status || ""}
          />
        ),
      },
      {
        key: "tasks",
        label: t("dashboardPage.tabTasks"),
        section: "team-tasks",
        render: () => (
          <DashboardTasksTab
            start={startDate.format("YYYY-MM-DD")}
            end={endDate.format("YYYY-MM-DD")}
          />
        ),
      },
      {
        key: "surveys",
        label: t("dashboardPage.tabSurveys"),
        section: "surveys",
        render: () => (
          <SurveyAnalysis
            start={startDate.format("YYYY-MM-DD")}
            end={endDate.format("YYYY-MM-DD")}
            channel={surveysAnalyticsParams.channel || ""}
            originId={surveysAnalyticsParams.originId || null}
          />
        ),
      },
      ...(canViewCampaigns
        ? [
            {
              key: "campaigns",
              label: t("dashboardPage.tabCampaigns", { defaultValue: "Campanhas" }),
              section: "origem",
              render: () => (
                <CampaignOriginDashboard
                  start={startDate.format("YYYY-MM-DD")}
                  end={endDate.format("YYYY-MM-DD")}
                  ids_status={campaignQueryParams.ids_status || []}
                  ids_origens={campaignQueryParams.ids_origens || []}
                  ids_campaigns={campaignQueryParams.ids_campaigns || []}
                  ids_whatsapp_campaigns={campaignQueryParams.ids_whatsapp_campaigns || []}
                />
              ),
            },
          ]
        : []),
    ];

    return base.filter((tab) => {
      if (Array.isArray(tab.segments) && tab.segments.length > 0) {
        return tab.segments.includes(segment);
      }
      return true;
    });
  }, [
    t,
    i18n.language,
    startDate,
    endDate,
    segment,
    canViewCampaigns,
    financeAttendanceReady,
    atendimentoFilterParams,
    liveAdvancedParams,
    campaignQueryParams,
    salesQueryParams,
    clientsQueryParams,
    produtosQueryParams,
    passagensQueryParams,
    surveysAnalyticsParams,
    handleNarrowToLast7Days,
  ]);

  const sectionByTab = React.useMemo(() => {
    const m = {};
    tabsDef.forEach((tab, idx) => {
      m[idx] = tab.section;
    });
    return m;
  }, [tabsDef]);

  const activeTab = React.useMemo(() => {
    if (!tabParam) return 0;
    const idx = tabsDef.findIndex((tab) => tab.key === tabParam);
    return idx >= 0 ? idx : -1;
  }, [tabParam, tabsDef]);

  const activeTabDef = tabsDef[activeTab];
  const activeSection = sectionByTab[activeTab];
  const showAdvancedFilters = activeSection === "live" || activeSection === "atendimento" || activeSection === "financeiro-atendimento";
  const showCampaignFilters = activeSection === "origem";
  const showSalesFilters = activeSection === "vendas";
  const showClientsFilters = activeSection === "clientes";
  const showProdutosFilters = activeSection === "produtos";
  const showPassagensFilters = activeSection === "venda-passagens";
  const showSurveysFilters = activeSection === "surveys";
  const periodFiltersPaused =
    (showAdvancedFilters && Boolean(advancedFilterState.all_period)) ||
    (showClientsFilters && Boolean(clientsFilterState.all_period));
  const canSharePresentation =
    activeSection && activeSection !== "live" && activeTabDef?.key !== "live";
  const shareSurveyId =
    activeTabDef?.key === "surveys" ? searchParams.get("survey") || "" : "";

  const advancedFilterSlot = React.useMemo(() => {
    if (showCampaignFilters) {
      return (
        <DashboardCampaignFiltersControl
          filterState={campaignFilterState}
          criteriaCount={campaignCriteriaCount}
          onOpen={() => setCampaignFiltersOpen(true)}
          onClear={handleClearCampaignFilters}
          section="origem"
          onApplySaved={handleCampaignFiltersApply}
        />
      );
    }
    if (showSalesFilters) {
      return (
        <DashboardSalesFiltersControl
          filterState={salesFilterState}
          criteriaCount={salesCriteriaCount}
          onOpen={() => setSalesFiltersOpen(true)}
          onClear={handleClearSalesFilters}
          section="vendas"
          onApplySaved={handleSalesFiltersApply}
        />
      );
    }
    if (showClientsFilters) {
      return (
        <DashboardClientsFiltersControl
          filterState={clientsFilterState}
          criteriaCount={clientsCriteriaCount}
          onOpen={() => setClientsFiltersOpen(true)}
          onClear={handleClearClientsFilters}
          section="clientes"
          onApplySaved={handleClientsFiltersApply}
        />
      );
    }
    if (showProdutosFilters) {
      return (
        <DashboardProdutosFiltersControl
          filterState={produtosFilterState}
          criteriaCount={produtosCriteriaCount}
          onOpen={() => setProdutosFiltersOpen(true)}
          onClear={handleClearProdutosFilters}
          section="produtos"
          onApplySaved={handleProdutosFiltersApply}
        />
      );
    }
    if (showPassagensFilters) {
      return (
        <DashboardPassagensFiltersControl
          filterState={passagensFilterState}
          criteriaCount={passagensCriteriaCount}
          onOpen={() => setPassagensFiltersOpen(true)}
          onClear={handleClearPassagensFilters}
          section="venda-passagens"
          onApplySaved={handlePassagensFiltersApply}
        />
      );
    }
    if (showSurveysFilters) {
      return (
        <DashboardSurveysFiltersControl
          filterState={surveysFilterState}
          criteriaCount={surveysCriteriaCount}
          onOpen={() => setSurveysFiltersOpen(true)}
          onClear={handleClearSurveysFilters}
          section="surveys"
          onApplySaved={handleSurveysFiltersApply}
        />
      );
    }
    if (!showAdvancedFilters) return null;
    return (
      <DashboardAdvancedFiltersControl
        filterState={advancedFilterState}
        criteriaCount={advancedCriteriaCount}
        onOpen={() => setAdvancedFiltersOpen(true)}
        onClear={handleClearAdvancedFilters}
        onRemoveChip={(next) => {
          setBootstrapJobId(null);
          persistAdvancedFilters(next);
        }}
        omitAllPeriodChip={
          Boolean(advancedFilterState.all_period) && activeSection !== "live"
        }
        section="atendimento"
        onApplySaved={handleAdvancedFiltersApply}
      />
    );
  }, [
    showAdvancedFilters,
    showCampaignFilters,
    showSalesFilters,
    showClientsFilters,
    showProdutosFilters,
    showPassagensFilters,
    showSurveysFilters,
    advancedFilterState,
    advancedCriteriaCount,
    handleClearAdvancedFilters,
    handleAdvancedFiltersApply,
    persistAdvancedFilters,
    campaignFilterState,
    campaignCriteriaCount,
    handleClearCampaignFilters,
    handleCampaignFiltersApply,
    salesFilterState,
    salesCriteriaCount,
    handleClearSalesFilters,
    handleSalesFiltersApply,
    clientsFilterState,
    clientsCriteriaCount,
    handleClearClientsFilters,
    handleClientsFiltersApply,
    produtosFilterState,
    produtosCriteriaCount,
    handleClearProdutosFilters,
    handleProdutosFiltersApply,
    passagensFilterState,
    passagensCriteriaCount,
    handleClearPassagensFilters,
    handlePassagensFiltersApply,
    surveysFilterState,
    surveysCriteriaCount,
    handleClearSurveysFilters,
    handleSurveysFiltersApply,
    activeSection,
  ]);

  if ((tabParam === "campaigns" || tabParam === "origin" || tabParam === "tasks") && permissionsLoading) {
    return <Box p={3}>Carregando permissões…</Box>;
  }

  // Alias legado: /dashboard/origin → /dashboard/campaigns
  if (tabParam === "origin" && canViewCampaigns) {
    return (
      <Navigate
        to={{ pathname: "/dashboard/campaigns", search: location.search, hash: location.hash }}
        replace
      />
    );
  }

  if (activeTab < 0) {
    const fallbackKey = tabsDef[0]?.key || "live";
    return (
      <Navigate
        to={{ pathname: `/dashboard/${fallbackKey}`, search: location.search, hash: location.hash }}
        replace
      />
    );
  }

  const handleTabChange = (_event, newValue) => {
    const key = tabsDef[newValue]?.key;
    if (key) {
      navigate({ pathname: `/dashboard/${key}`, search: location.search, hash: location.hash });
    }
  };

  const handleExport = async () => {
    const section = sectionByTab[activeTab];
    if (!section) return;
    if (section === "live") return;

    const atendimentoExportParams = {
      ids_origens: atendimentoFilterParams.ids_origens,
      ids_tipos: atendimentoFilterParams.ids_tipos,
      ids_subtipo1: atendimentoFilterParams.ids_subtipo1,
      ids_subtipo2: atendimentoFilterParams.ids_subtipo2,
      ids_subtipo3: atendimentoFilterParams.ids_subtipo3,
      ids_status: atendimentoFilterParams.ids_status,
      ids_usuarios: atendimentoFilterParams.ids_usuarios,
      ids_agentes: atendimentoFilterParams.ids_agentes,
      ids_usuarios_interacao: atendimentoFilterParams.ids_usuarios_interacao,
      ids_agentes_interacao: atendimentoFilterParams.ids_agentes_interacao,
      tags: atendimentoFilterParams.tags,
      tipo_responsavel: atendimentoFilterParams.tipo_responsavel,
      date_field: atendimentoFilterParams.date_field,
      all_period: atendimentoFilterParams.all_period ? 1 : undefined,
      ids_produtos: atendimentoFilterParams.ids_produtos,
      ids_campaigns: atendimentoFilterParams.ids_campaigns,
      ids_whatsapp_campaigns: atendimentoFilterParams.ids_whatsapp_campaigns,
      ids_motivos: atendimentoFilterParams.ids_motivos,
      ids_potenciais: atendimentoFilterParams.ids_potenciais,
      ops_preset: atendimentoFilterParams.ops_preset,
      stale_days: atendimentoFilterParams.stale_days,
      ...(activeTabDef?.key === "service" ? { tipo_scope: "relacionamento" } : {}),
    };

    const params = {
      start: startDate.format("YYYY-MM-DD"),
      end: endDate.format("YYYY-MM-DD"),
      ...(section === "atendimento" || section === "financeiro-atendimento"
        ? atendimentoExportParams
        : {}),
      ...(section === "vendas" ? { ...salesQueryParams } : {}),
      ...(section === "clientes" ? { ...clientsQueryParams } : {}),
      ...(section === "produtos" ? { ...produtosQueryParams } : {}),
      ...(section === "venda-passagens" ? { ...passagensQueryParams } : {}),
      ...(section === "origem" ? { ...campaignQueryParams } : {}),
    };

    try {
      await exportDashboardCsv(
        section,
        params,
        `dashboard_${section}_${startDate.format("YYYYMMDD")}_${endDate.format("YYYYMMDD")}.csv`
      );
    } catch (err) {
      console.error("Erro ao exportar:", err);
      const code = err?.code;
      const msg =
        code === "DASHBOARD_EXPORT_STILL_PROCESSING"
          ? t("dashboardPage.exportStillProcessing", {
              defaultValue: "O relatório ainda está sendo gerado. Aguarde a notificação e tente exportar de novo.",
            })
          : code === "DASHBOARD_EXPORT_UNSUPPORTED"
            ? t("dashboardPage.exportUnsupported", {
                defaultValue: "Esta aba não tem exportação de CSV.",
              })
            : err?.userFacing ||
              err?.response?.data?.message ||
              err?.message ||
              t("dashboardPage.exportFailed");
      alert(msg);
    }
  };

  return (
    <DashboardJobsProvider>
    <DashboardJobBootstrapContext.Provider value={bootstrapJobId}>
    <DashboardShareReadinessProvider activeTabKey={activeTabDef?.key}>
    <LocalizationProvider
      key={datePickerLocale.adapterLocale}
      dateAdapter={AdapterDayjs}
      adapterLocale={datePickerLocale.adapterLocale}
      localeText={datePickerLocale.localeText}
    >
      <Box
        className="tour-dashboard-root"
        sx={{
          padding: 1,
          paddingBottom: '150px !important',
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
        }}
      >
        <DashboardPageToolbar
          title={t("dashboardPage.title")}
          showPeriod={sectionByTab[activeTab] !== "live"}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          maxSelectableDate={maxSelectableDate}
          isMobile={isMobile}
          filterSlot={advancedFilterSlot}
          periodDisabled={periodFiltersPaused}
          periodDisabledHint={
            periodFiltersPaused
              ? showClientsFilters
                ? t("dashboardPage.clientsFilters.allPeriodToolbarHint", {
                    defaultValue:
                      "As datas da barra estão pausadas. Os números consideram todo o histórico de clientes.",
                  })
                : t("dashboardPage.filters.allPeriodToolbarHint", {
                    defaultValue:
                      "As datas da barra estão pausadas. Os números consideram todo o histórico das solicitações em aberto.",
                  })
              : null
          }
          onPeriodChipClick={
            periodFiltersPaused
              ? () => {
                  if (showClientsFilters) setClientsFiltersOpen(true);
                  else setAdvancedFiltersOpen(true);
                }
              : null
          }
          showCaches={sectionByTab[activeTab] !== "live"}
          onCachesOpen={() => setCachesOpen(true)}
          showShare={canSharePresentation}
          onShareOpen={() => setShareOpen(true)}
          showExport={sectionByTab[activeTab] !== "live"}
          onExport={handleExport}
          exportDisabled={
            sectionByTab[activeTab] === "surveys" ||
            sectionByTab[activeTab] === "live" ||
            sectionByTab[activeTab] === "team-tasks"
          }
        />

        <AdvancedSearchModal
          open={advancedFiltersOpen}
          onClose={() => setAdvancedFiltersOpen(false)}
          variant="dashboard"
          applyLabel={t("dashboardPage.filters.apply", { defaultValue: "Aplicar filtros" })}
          showSavedFiltersManager={false}
          initialAdvanced={filterStateToModalInitial({
            ...advancedFilterState,
            ...(advancedFilterState.all_period
              ? { data_inicio: "", data_fim: "" }
              : {
                  data_inicio: startDate.format("YYYY-MM-DD"),
                  data_fim: endDate.format("YYYY-MM-DD"),
                }),
          })}
          onApply={handleAdvancedFiltersApply}
        />

        <DashboardCampaignFiltersModal
          open={campaignFiltersOpen}
          onClose={() => setCampaignFiltersOpen(false)}
          initialState={campaignFilterState}
          onApply={handleCampaignFiltersApply}
        />

        <DashboardSalesFiltersModal
          open={salesFiltersOpen}
          onClose={() => setSalesFiltersOpen(false)}
          initialState={salesFilterState}
          onApply={handleSalesFiltersApply}
        />

        <DashboardClientsFiltersModal
          open={clientsFiltersOpen}
          onClose={() => setClientsFiltersOpen(false)}
          initialState={clientsFilterState}
          onApply={handleClientsFiltersApply}
        />

        <DashboardProdutosFiltersModal
          open={produtosFiltersOpen}
          onClose={() => setProdutosFiltersOpen(false)}
          initialState={produtosFilterState}
          onApply={handleProdutosFiltersApply}
        />

        <DashboardPassagensFiltersModal
          open={passagensFiltersOpen}
          onClose={() => setPassagensFiltersOpen(false)}
          initialState={passagensFilterState}
          onApply={handlePassagensFiltersApply}
        />

        <DashboardSurveysFiltersModal
          open={surveysFiltersOpen}
          onClose={() => setSurveysFiltersOpen(false)}
          initialState={surveysFilterState}
          onApply={handleSurveysFiltersApply}
        />

        <Box>
          {billingSignal ? (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => navigate("/settings/billing")}>
                  {t("dashboardPage.managePlan", { defaultValue: "Gerenciar plano" })}
                </Button>
              }
            >
              {billingSignal?.payload?.message ||
                t("dashboardPage.billingSignal", {
                  defaultValue: "Detectamos oportunidade de upgrade/add-on com base no uso atual.",
                })}
            </Alert>
          ) : null}
          <DashboardBackgroundJobsPanel />
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: theme.palette.divider,
              "& .MuiTab-root": {
                color: theme.palette.text.primary,
              },
              "& .Mui-selected": {
                color: theme.palette.primary.main,
              },
            }}
            variant={isMobile ? "scrollable" : "standard"} // Habilita scroll no mobile
            scrollButtons={isMobile ? "auto" : false} // Mostra botões de rolagem no mobile
          >
            {tabsDef.map((tab, index) => (
              <Tab
                key={tab.key}
                label={tab.label}
                value={index}
                component={Link}
                to={{ pathname: `/dashboard/${tab.key}`, search: location.search, hash: location.hash }}
              />
            ))}
            {/* <Tab label="Pesquisas" /> */}
            {/* <Tab label="Bot" /> */}
            {/* <Tab label="Campanhas" /> */}
            {/* <Tab label="Xpay" /> */}
          </Tabs>

          <Box className="tour-dashboard-tab-panel" sx={{ marginTop: 2 }}>
            {tabsDef[activeTab]?.render?.()}
            {/* {activeTab === 7 && <BotAnalytics />} */}
            {/* {activeTab === 8 && <CampaignAnalysis />} */}
            {/* {activeTab === 10 && <Xpay />} */}
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
    <DashboardPresentationShareDialog
      open={shareOpen}
      onClose={() => setShareOpen(false)}
      tabKey={activeTabDef?.key}
      tabLabel={activeTabDef?.label}
      start={startDate.format("YYYY-MM-DD")}
      end={endDate.format("YYYY-MM-DD")}
      surveyId={shareSurveyId || null}
    />
    <DashboardCachesDialog open={cachesOpen} onClose={() => setCachesOpen(false)} />
    </DashboardShareReadinessProvider>
    </DashboardJobBootstrapContext.Provider>
    </DashboardJobsProvider>
  );
};

export default Dashboard;
